/**
 * Task Stream Handlers — TASK_STREAM (Supabase realtime updates via server)
 */

import type { Socket } from "socket.io-client";
import { EVENTS } from "@/constants/events";
import { useTaskStore, vouchForOwnTask } from "../taskStore";
import { useHistoryStore } from "../historyStore";
import type { PersistedTask } from "../types";

/** Map any server lifecycle state to the correct stats bucket. Exhaustive — no state falls through. */
export function lifecycleToBucket(
  lc: string
): "queued" | "working" | "needsInput" | "completed" {
  switch (lc) {
    case "scheduled":
      return "queued";
    case "processing":
      return "working";
    case "awaiting":
      return "needsInput";
    case "error":
      return "needsInput";
    case "queued":
      return "queued";
    default:
      return "completed"; // idle, completed, unknown
  }
}

// Short-lived set of recently destroyed task IDs — prevents late UPDATE events from
// applying stats deltas after TASK_DESTROY already decremented counters.
const _destroyedTaskIds = new Set<string>();
const DESTROY_GUARD_TTL_MS = 10_000;

/** Mark a task ID as recently destroyed (used by taskLifecycleHandlers too). */
export function markTaskDestroyed(taskId: string): void {
  _destroyedTaskIds.add(taskId);
  setTimeout(() => _destroyedTaskIds.delete(taskId), DESTROY_GUARD_TTL_MS);
}

export function registerTaskStreamHandlers(socket: Socket): () => void {
  const taskStore = useTaskStore;
  const historyStore = useHistoryStore;

  // NOT PayloadOf: TaskStreamPayloadSchema embeds the DEAD shared Task shape (agentId,
  // Date fields, no lifecycleState / pendingDetails / parentSessionId) and names the
  // previous row `old`, while the wire sends `oldTask` — the schema contradicts the
  // wire (s2-07b finding, see PR s2-07c). Inline until the schema is fixed.
  const handleTaskStream = (data: {
    eventType: "INSERT" | "UPDATE" | "DELETE";
    task: {
      id: string;
      userId: string;
      agentSlug: string;
      parentSessionId?: string;
      title?: string;
      status: string;
      lifecycleState?: string;
      pendingDetails?: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
      lastActivityAt: string;
      /**
       * Engines T5 (tasks.resume_at). OMITTED when the row does not carry the
       * column: absent says nothing, null says "not waiting on a plan".
       */
      resumeAt?: string | null;
      /** Several accounts (tasks.engine_slot_id). Omitted with the column, as resumeAt. */
      engineSlotId?: string | null;
    };
    oldTask?: { id: string; lifecycleState?: string; status?: string };
  }) => {
    const hs = historyStore.getState();
    const carriesResumeAt = "resumeAt" in data.task;
    const resumeAt = data.task.resumeAt ?? null;
    // Several accounts: each Claude Code dispatch records its slot, so the
    // header's account follows the task live. Absent says nothing, as above.
    const slot =
      "engineSlotId" in data.task
        ? { engineSlotId: data.task.engineSlotId ?? null }
        : {};

    if (data.eventType === "INSERT") {
      hs._setTaskHistory((prev) => {
        if (prev.some((t) => t.id === data.task.id)) return prev;
        const newTask: PersistedTask = {
          id: data.task.id,
          userId: data.task.userId,
          agentSlug: data.task.agentSlug,
          parentSessionId: data.task.parentSessionId,
          title: data.task.title,
          status: data.task.status,
          lifecycleState: data.task
            .lifecycleState as PersistedTask["lifecycleState"],
          pendingDetails: data.task
            .pendingDetails as PersistedTask["pendingDetails"],
          createdAt: data.task.createdAt,
          updatedAt: data.task.updatedAt,
          lastActivityAt: data.task.lastActivityAt,
          ...(carriesResumeAt ? { resumeAt } : {}),
          ...slot,
        };
        return [newTask, ...prev];
      });
      // The owner's stream carried the row: proof it exists and is theirs.
      vouchForOwnTask(data.task.id);

      hs._setTaskStats((prev) => {
        if (!prev) return prev;
        const n = {
          ...prev,
          total: prev.total + 1,
          byStatus: { ...prev.byStatus },
          byLifecycle: { ...prev.byLifecycle },
        };
        if (data.task.status === "active") n.byStatus.active++;
        else if (data.task.status === "paused") n.byStatus.paused++;
        const bucket = lifecycleToBucket(data.task.lifecycleState || "idle");
        n.byLifecycle[bucket]++;
        return n;
      });
    } else if (data.eventType === "UPDATE") {
      hs._setTaskHistory((prev) =>
        prev.map((t) =>
          t.id === data.task.id
            ? {
                ...t,
                title: data.task.title,
                status: data.task.status,
                lifecycleState: data.task
                  .lifecycleState as PersistedTask["lifecycleState"],
                pendingDetails: data.task
                  .pendingDetails as PersistedTask["pendingDetails"],
                updatedAt: data.task.updatedAt,
                lastActivityAt: data.task.lastActivityAt,
                ...(carriesResumeAt ? { resumeAt } : {}),
                ...slot,
              }
            : t
        )
      );

      taskStore.getState()._mutateTasks((tasks) => {
        const task = tasks.get(data.task.id);
        if (!task) return;
        if (data.task.title) task.title = data.task.title;
        // Trust server's last_activity_at (maintained by Postgres triggers).
        // This keeps the home-page sort key in sync without client-side bumps.
        task.lastActivityAt = new Date(data.task.lastActivityAt);
        // Engines T5: when the task picks up by itself. Only a value: a card is
        // never inferred settled from it (rows written before and after a turn
        // start land here out of order). A card leaves on its own row's settle
        // (useInteractionsRealtime -> reconcileServerInteraction), AGENT_START,
        // or this client's tap; a value left behind expires by itself
        // (isWaiting).
        if (carriesResumeAt) task.resumeAt = resumeAt;
        if ("engineSlotId" in slot) task.engineSlotId = slot.engineSlotId;
      });
      vouchForOwnTask(data.task.id);

      // Use oldTask from realtime payload for stats diff — works regardless of pagination.
      // Skip if: no oldTask (can't diff), or task was recently destroyed (prevents double-decrement).
      if (data.oldTask && !_destroyedTaskIds.has(data.task.id)) {
        const oldLC = data.oldTask.lifecycleState || "idle";
        const newLC = data.task.lifecycleState || "idle";
        const oldStatus = data.oldTask.status || "active";
        const newStatus = data.task.status;
        const lcChanged = oldLC !== newLC;
        const statusChanged = oldStatus !== newStatus;

        if (lcChanged || statusChanged) {
          hs._setTaskStats((prev) => {
            if (!prev) return prev;
            const n = {
              ...prev,
              byLifecycle: { ...prev.byLifecycle },
              byStatus: { ...prev.byStatus },
            };
            if (lcChanged) {
              const oldBucket = lifecycleToBucket(oldLC);
              const newBucket = lifecycleToBucket(newLC);
              n.byLifecycle[oldBucket] = Math.max(
                0,
                n.byLifecycle[oldBucket] - 1
              );
              n.byLifecycle[newBucket]++;
            }
            if (statusChanged) {
              if (oldStatus === "active")
                n.byStatus.active = Math.max(0, n.byStatus.active - 1);
              else if (oldStatus === "paused")
                n.byStatus.paused = Math.max(0, n.byStatus.paused - 1);
              if (newStatus === "active") n.byStatus.active++;
              else if (newStatus === "paused") n.byStatus.paused++;
            }
            return n;
          });
        }
      }
    } else if (data.eventType === "DELETE") {
      markTaskDestroyed(data.task.id);
      hs._setTaskHistory((prev) => prev.filter((t) => t.id !== data.task.id));
      taskStore.getState()._mutateTasks((tasks) => {
        tasks.delete(data.task.id);
      });
      hs._setTaskStats((prev) => {
        if (!prev) return prev;
        const n = {
          ...prev,
          total: Math.max(0, prev.total - 1),
          byStatus: { ...prev.byStatus },
          byLifecycle: { ...prev.byLifecycle },
        };
        if (data.task.status === "active")
          n.byStatus.active = Math.max(0, n.byStatus.active - 1);
        else if (data.task.status === "paused")
          n.byStatus.paused = Math.max(0, n.byStatus.paused - 1);
        const bucket = lifecycleToBucket(data.task.lifecycleState || "idle");
        n.byLifecycle[bucket] = Math.max(0, n.byLifecycle[bucket] - 1);
        return n;
      });
      if (taskStore.getState().selectedTaskId === data.task.id) {
        taskStore.getState()._setSelectedTaskId(null);
      }
    }
  };

  socket.on(EVENTS.TASK_STREAM, handleTaskStream);

  return () => {
    socket.off(EVENTS.TASK_STREAM, handleTaskStream);
  };
}
